import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical,
  Lock,
  MoreHorizontal,
  Pencil,
  Plus,
  Share2,
  Trash2,
} from "lucide-react";
import type { ViewEntityType } from "@colab/shared";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth-context";
import { useViews } from "@/features/admin/views/use-views";
import { useReorderViews } from "@/features/admin/views/use-reorder-views";
import type { SavedView } from "@/features/admin/views/types";
import { buildViewParamUpdate } from "@/features/admin/views/active-view";
import { SaveViewDialog, type SaveViewInitialState } from "./save-view-dialog";
import { RenameViewDialog } from "./rename-view-dialog";
import { DeleteViewDialog } from "./delete-view-dialog";

// Horizontal pill-tab strip for Saved Views. Drives URL state via
// `?view=<id>`. Reorder via @dnd-kit (Pattern C optimism in the
// useReorderViews hook). System views (is_system=true) render a lock
// glyph; shared views render a share glyph; private views render their
// owner-only state implicitly.
//
// SPA port of components/shared/views/SavedViewTabs.tsx. Differences:
//   - URL state via react-router useSearchParams (not Next router.push)
//   - View list loaded via useViews(entityType), not server-prop
//   - Save / rename / delete dialogs render inline (legacy hoisted them
//     into the parent server-component shell)

type Props = {
  entityType: ViewEntityType;
  /** Filter + columns + sort the page currently has loaded — passed to "Save as". */
  saveInitialState: SaveViewInitialState;
};

export function SavedViewTabs({ entityType, saveInitialState }: Props) {
  const session = useAuth().session;
  const currentUserId = session?.userId ?? null;
  const canManageShared = !!session?.permissions.includes("settings.write");

  const [searchParams, setSearchParams] = useSearchParams();
  const activeViewId = searchParams.get("view");

  const viewsQuery = useViews(entityType);
  const views = useMemo(() => viewsQuery.data ?? [], [viewsQuery.data]);

  // Local optimistic order — DnD feels instant; the mutation hook handles
  // server reconciliation + revert on error.
  const [order, setOrder] = useState<SavedView[]>(views);
  useEffect(() => setOrder(views), [views]);

  const reorderMutation = useReorderViews();
  const [saveOpen, setSaveOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const ids = useMemo(() => order.map((v) => v.id), [order]);

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = order.findIndex((v) => v.id === active.id);
    const newIdx = order.findIndex((v) => v.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(order, oldIdx, newIdx);
    setOrder(next);
    // Exclude system views from the ordered-ids payload. The reorder_views RPC
    // raises 42501 if any system view id is included (system views are fixed in
    // position by policy). Sending only non-system ids is safe because the RPC
    // only updates positions for the ids it receives; system views retain their
    // existing positions.
    reorderMutation.mutate({
      entityType,
      orderedIds: next.filter((v) => !v.is_system).map((v) => v.id),
    });
  }

  function selectView(id: string | null) {
    const next = buildViewParamUpdate(searchParams, id);
    setSearchParams(next, { replace: true });
  }

  if (viewsQuery.isLoading) {
    return <div className="mb-4 h-8" aria-hidden />;
  }

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext items={ids} strategy={horizontalListSortingStrategy}>
          <ul className="flex flex-wrap items-center gap-1.5">
            {order.map((v) => (
              <li key={v.id}>
                <SortableTab
                  view={v}
                  entityType={entityType}
                  isActive={v.id === activeViewId}
                  currentUserId={currentUserId}
                  canManageShared={canManageShared}
                  onSelect={() => selectView(v.id)}
                />
              </li>
            ))}
          </ul>
        </SortableContext>
      </DndContext>

      {activeViewId && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => selectView(null)}
        >
          Clear
        </Button>
      )}

      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
        onClick={() => setSaveOpen(true)}
      >
        <Plus className="h-3.5 w-3.5" />
        Save view as…
      </Button>

      <SaveViewDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        entityType={entityType}
        initialState={saveInitialState}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sortable pill.
// ---------------------------------------------------------------------------

function SortableTab({
  view,
  entityType,
  isActive,
  currentUserId,
  canManageShared,
  onSelect,
}: {
  view: SavedView;
  entityType: ViewEntityType;
  isActive: boolean;
  currentUserId: string | null;
  canManageShared: boolean;
  onSelect: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: view.id });

  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const canEdit = canEditView(view, currentUserId, canManageShared);
  const canDelete = !view.is_system && canEdit;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "group inline-flex h-8 items-center gap-0.5 rounded-full border bg-card pl-1 pr-1 text-xs transition-colors",
        isActive
          ? "border-accent/50 bg-accent/10 text-foreground shadow-sm"
          : "border-border/70 text-muted-foreground hover:bg-muted/40 hover:text-foreground",
      )}
    >
      {!view.is_system && (
        <button
          type="button"
          data-saved-view-handle=""
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
          className="ml-0.5 flex h-5 w-3.5 cursor-grab items-center justify-center text-muted-foreground/60 opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100"
        >
          <GripVertical className="h-3 w-3" />
        </button>
      )}

      <button
        type="button"
        onClick={onSelect}
        className="flex h-7 items-center gap-1.5 rounded-full px-2.5 font-medium tracking-tight"
      >
        {view.scope === "shared" && !view.is_system && (
          <Share2 className="h-3 w-3 opacity-70" aria-label="Shared view" />
        )}
        {view.is_system && (
          <Lock className="h-3 w-3 opacity-70" aria-label="System view" />
        )}
        <span className="max-w-[200px] truncate">{view.name}</span>
      </button>

      {isActive && (canEdit || canDelete) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="View options"
              className="ml-0.5 flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:bg-background hover:text-foreground"
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem
              onClick={() => setRenameOpen(true)}
              disabled={!canEdit}
            >
              <Pencil className="mr-2 h-3.5 w-3.5" />
              Rename
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setDeleteOpen(true)}
              disabled={!canDelete}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-3.5 w-3.5" />
              Delete view
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <RenameViewDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        viewId={view.id}
        entityType={entityType}
        currentName={view.name}
      />
      <DeleteViewDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        viewId={view.id}
        entityType={entityType}
        viewName={view.name}
      />
    </div>
  );
}

function canEditView(
  view: SavedView,
  currentUserId: string | null,
  canManageShared: boolean,
): boolean {
  if (view.is_system) return false;
  if (view.scope === "shared") return canManageShared;
  if (view.created_by && currentUserId && view.created_by === currentUserId) {
    return true;
  }
  return canManageShared;
}
